/**
 * Graphics quality tiers + auto-detection for mobile/desktop.
 * Stages MUST scale their particle/entity counts and effects using this profile.
 */
import type { QualityPreset } from './run';

export type QualityTier = 'low' | 'medium' | 'high';

export interface QualityProfile {
  tier: QualityTier;
  isMobile: boolean;
  isTouch: boolean;
  /** Max device pixel ratio for this tier (dynamic resolution may go lower). */
  maxPixelRatio: number;
  antialias: boolean;
  shadows: boolean;
  shadowMapSize: number;
  /** Post-processing (bloom, vignette...). */
  postfx: boolean;
  bloom: boolean;
  /** Multiply particle counts by this. */
  particleScale: number;
  /** Multiply ambient entity/foliage counts by this. */
  densityScale: number;
  /** Multiply far-plane / fog distances by this. */
  drawDistance: number;
  /** Max geometry detail hint (segments multiplier). */
  geometryDetail: number;
  /** Lowest pixel ratio dynamic resolution may drop to (keeps phones from going blurry). */
  minPixelRatio: number;
  /** MSAA samples for the post-fx composer (0 = off; high-DPR phones don't need it). */
  msaa: number;
  /** Detected device class (for debugging / QA). */
  device: string;
}

const TIERS: Record<QualityTier, Omit<QualityProfile, 'isMobile' | 'isTouch' | 'minPixelRatio' | 'msaa' | 'device'>> = {
  low: {
    tier: 'low',
    maxPixelRatio: 1,
    antialias: false,
    shadows: false,
    shadowMapSize: 512,
    postfx: false,
    bloom: false,
    particleScale: 0.4,
    densityScale: 0.45,
    drawDistance: 0.6,
    geometryDetail: 0.5,
  },
  medium: {
    tier: 'medium',
    maxPixelRatio: 1.5,
    antialias: true,
    shadows: true,
    shadowMapSize: 1024,
    postfx: true,
    bloom: true,
    particleScale: 0.7,
    densityScale: 0.7,
    drawDistance: 0.8,
    geometryDetail: 0.75,
  },
  high: {
    tier: 'high',
    maxPixelRatio: 2,
    antialias: true,
    shadows: true,
    shadowMapSize: 2048,
    postfx: true,
    bloom: true,
    particleScale: 1,
    densityScale: 1,
    drawDistance: 1,
    geometryDetail: 1,
  },
};

export function detectMobile(): { isMobile: boolean; isTouch: boolean } {
  if (typeof navigator === 'undefined') return { isMobile: false, isTouch: false };
  const ua = navigator.userAgent || '';
  const isTouch = 'ontouchstart' in window || (navigator.maxTouchPoints ?? 0) > 0;
  const uaMobile = /Android|iPhone|iPad|iPod|Mobile|Silk|Kindle/i.test(ua);
  // iPadOS reports as Mac; treat touch Macs as mobile.
  const iPadOS = /Macintosh/.test(ua) && isTouch;
  const small = Math.min(window.screen?.width ?? 1920, window.screen?.height ?? 1080) < 820;
  return { isMobile: uaMobile || iPadOS || (isTouch && small), isTouch };
}

/**
 * Device class heuristics. Phones used to be forced to the low tier at 1x resolution, which
 * looked terrible on modern high-DPR screens (an iPhone 16 Pro rendered at 1/3 native res).
 * Now: capable phones get the high tier at ~2x with bloom; dynamic resolution + the runtime
 * governor (render.ts) back off only if the frame rate actually suffers.
 */
export function detectDeviceClass(): { tier: QualityTier; device: string } {
  if (typeof navigator === 'undefined') return { tier: 'high', device: 'unknown' };
  const ua = navigator.userAgent || '';
  const dpr = window.devicePixelRatio || 1;
  const long = Math.max(window.screen?.width ?? 0, window.screen?.height ?? 0);
  const iPhone = /iPhone|iPod/.test(ua);
  const iPad = /iPad/.test(ua) || (/Macintosh/.test(ua) && (navigator.maxTouchPoints ?? 0) > 1);
  if (iPhone) {
    // Pro/Pro Max & 15/16-series (A16+) have long side >= 852pt @3x; 12/13 (844/926) are A14/A15.
    if (dpr >= 3 && long >= 852) return { tier: 'high', device: `iphone-a16+ (${long}pt)` };
    if (dpr >= 3) return { tier: 'medium', device: `iphone-a14/15 (${long}pt)` };
    return { tier: 'low', device: `iphone-older (${long}pt @${dpr}x)` };
  }
  if (iPad) return { tier: 'high', device: 'ipad' };
  const { isMobile } = detectMobile();
  if (!isMobile) return { tier: 'high', device: 'desktop' };
  const mem = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 4;
  const cores = navigator.hardwareConcurrency ?? 4;
  if (mem >= 8 && cores >= 8) return { tier: 'high', device: `android-high (${mem}GB/${cores}c)` };
  if (mem >= 4 && cores >= 6) return { tier: 'medium', device: `android-mid (${mem}GB/${cores}c)` };
  return { tier: 'low', device: `android-low (${mem}GB/${cores}c)` };
}

const DOWNGRADE_KEY = 'evo.autoTierCap.v1';
const ORDER: QualityTier[] = ['low', 'medium', 'high'];

/** Remember that this device couldn't hold frame rate on a tier (set by the runtime governor). */
export function capAutoTier(tier: QualityTier): void {
  try {
    localStorage.setItem(DOWNGRADE_KEY, tier);
  } catch {
    /* ignore */
  }
}

function autoTierCap(): QualityTier | undefined {
  try {
    const v = localStorage.getItem(DOWNGRADE_KEY);
    return v === 'low' || v === 'medium' || v === 'high' ? v : undefined;
  } catch {
    return undefined;
  }
}

export function resolveQuality(preset: QualityPreset, override?: string | null): QualityProfile {
  const { isMobile, isTouch } = detectMobile();
  const detected = detectDeviceClass();
  let tier: QualityTier;
  if (override === 'low' || override === 'medium' || override === 'high') tier = override;
  else if (preset !== 'auto') tier = preset;
  else {
    tier = detected.tier;
    const cap = autoTierCap();
    if (cap && ORDER.indexOf(cap) < ORDER.indexOf(tier)) tier = cap;
  }
  // Software rendering (headless QA / no GPU) → low.
  if (preset === 'auto' && !override && isSoftwareRenderer()) tier = 'low';
  const base = { ...TIERS[tier] };
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  let minPixelRatio = 0.6;
  let msaa = base.antialias ? 4 : 0;
  if (isMobile) {
    // Phones: never render below ~1x CSS pixels on high-DPR screens (it looks smeared), skip MSAA
    // (2x DPR already hides aliasing) and trim the heaviest costs a little.
    msaa = 0;
    if (tier === 'high') {
      base.maxPixelRatio = Math.min(dpr, 2);
      base.shadowMapSize = 1024;
      base.densityScale = 0.8;
      base.particleScale = 0.75;
      base.drawDistance = 0.85;
      minPixelRatio = Math.min(base.maxPixelRatio, 1.25);
    } else if (tier === 'medium') {
      base.maxPixelRatio = Math.min(dpr, 1.75);
      base.shadows = false;
      minPixelRatio = Math.min(base.maxPixelRatio, 1);
    } else {
      base.maxPixelRatio = Math.min(dpr, 1.5);
      minPixelRatio = Math.min(base.maxPixelRatio, 0.9);
    }
  }
  return { ...base, tier, isMobile, isTouch, minPixelRatio, msaa, device: detected.device };
}

let softwareCache: boolean | undefined;
export function isSoftwareRenderer(): boolean {
  if (softwareCache !== undefined) return softwareCache;
  try {
    const c = document.createElement('canvas');
    const gl = c.getContext('webgl2') || c.getContext('webgl');
    if (!gl) return (softwareCache = true);
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    const name = ext ? String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)) : '';
    softwareCache = /SwiftShader|llvmpipe|Software|Basic Render/i.test(name);
  } catch {
    softwareCache = false;
  }
  return softwareCache;
}
